import React, { useCallback, useEffect, useMemo } from 'react';
import { useWidget } from '../../state/dashboard';
import useFetchMetrics from './useFetchMetrics';
import RemoveWidget from '../../control/RemoveWidget';
import { Retry } from '@src/components/common';
import { Alert, Space } from 'antd';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import BarChart from './BarChart';
import { viewerMetrics } from './constants';
import { formatViewersData } from './utils';
import useParams from './useParams';
import moment from 'moment';
import { GranularityMap } from '@src/components/common/GranularitySelect/utils';
import { useDispatch } from 'react-redux';
import { setSlice } from '@store/trends/birdChart/actions';
import { sliceAreaAtom } from '@src/components/metrics-panel-hz/atoms';
import { useSetAtom } from 'jotai';
import { activeTabAtom } from '@src/components/dimension-panel-hz/atoms';
import { Tabs } from '@store/trends/base/reducer';

function Viewers({ wid }) {
  const widgetHandler = useWidget({ wid });
  const { isIntervalValid, granularity, selectedIndex, setSelectedIndex } = useParams();
  const dispatch = useDispatch();
  const setSliceArea = useSetAtom(sliceAreaAtom);
  const setActiveTab = useSetAtom(activeTabAtom);

  const { isFetching, isError, data, refetch, queued } = useFetchMetrics({
    wid,
    metrics: viewerMetrics,
    granularity,
    tag: 'viewers',
    enabled: isIntervalValid,
  });

  useEffect(() => {}, []);

  const chartOption = useMemo(() => {
    if (data) {
      const formattedData = formatViewersData(data.data, selectedIndex);
      return formattedData;
    }
    return {};
  }, [data, selectedIndex]);

  const handleBarClick = useCallback(
    ({ value, dataIndex }) => {
      // toggle
      if (dataIndex === selectedIndex) {
        setSliceArea([]);
        dispatch(setSlice([0, 100], [0, 0]));
        setSelectedIndex(null);
      } else {
        const { step, unit } = GranularityMap[granularity];
        const range = [moment(value).valueOf(), moment(value).add(step, unit).valueOf()];
        // sets slice on charts
        setSliceArea(range);
        dispatch(setSlice([0, 100], range));
        // highlight selected bar
        setSelectedIndex(dataIndex);
        // switch to impacted viewers tab
        setActiveTab(Tabs.TopImpactedViewers);
      }
    },
    [granularity, selectedIndex],
  );

  return (
    <WidgetCardWrapper
      wid={wid}
      title={'Viewers'}
      style={{ gridRow: 'span 14' }}
      extra={
        <Space>
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      {isIntervalValid ? (
        <Retry
          isLoading={isFetching}
          showError={isError}
          fetchData={refetch}
          progress={true}
          queued={queued}
        >
          <div style={{ position: 'relative', height: '100%' }}>
            <BarChart chartOption={chartOption} handleBarClick={handleBarClick} />
          </div>
        </Retry>
      ) : (
        <div style={{ position: 'relative', height: '100%' }}>
          <Alert
            description="Impacted Viewers are only supported for 24 hour range within 30 days."
            type="info"
          />
        </div>
      )}
    </WidgetCardWrapper>
  );
}

export default Viewers;
